import { ChangeEvent } from 'react';

import { Input, Select } from '@/shared/design-system';

import { AddressParts } from './contact-address-fields.styled';

interface RegionOption {
  value: string;
  label: string;
}

export interface ContactAddressValue {
  country: string | null;
  city: string | null;
  street: string | null;
  street2?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

export interface ContactAddressFieldsProps<T extends ContactAddressValue> {
  address: T;
  index: number;
  regionOptions: RegionOption[];
  isCountriesLoading: boolean;
  onChange: (_next: T) => void;
}

/** The part inputs of one address row. The country row and the delete control stay with the caller,
 * which owns their per-surface layout and semantics. */
export const ContactAddressFields = <T extends ContactAddressValue>({
  address,
  index,
  regionOptions,
  isCountriesLoading,
  onChange,
}: ContactAddressFieldsProps<T>) => {
  const handleInput = (part: keyof ContactAddressValue) => (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ ...address, [part]: event.target.value });

  return (
    <AddressParts>
      <Select
        size="lg"
        options={regionOptions}
        value={address.state || null}
        placeholder="Enter state/province"
        ariaLabel={`State/province ${index + 1}`}
        disabled={!address.country || isCountriesLoading}
        showSearch
        onChange={(value) => onChange({ ...address, state: value })}
      />
      <Input
        size="lg"
        placeholder="Enter city"
        aria-label={`City ${index + 1}`}
        value={address.city ?? ''}
        onChange={handleInput('city')}
      />
      <Input
        size="lg"
        placeholder="Enter address line 1"
        aria-label={`Address 1 ${index + 1}`}
        value={address.street ?? ''}
        onChange={handleInput('street')}
      />
      <Input
        size="lg"
        placeholder="Apt, suite, unit (optional)"
        aria-label={`Address 2 ${index + 1}`}
        value={address.street2 ?? ''}
        onChange={handleInput('street2')}
      />
      <Input
        size="lg"
        placeholder="Enter zip/postal code"
        aria-label={`Zip/postal code ${index + 1}`}
        value={address.postalCode ?? ''}
        onChange={handleInput('postalCode')}
      />
    </AddressParts>
  );
};
